import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { XCircle, Calendar, ArrowLeft } from 'lucide-react';
import { Card, Button } from '@caruso/ui';
import { eventsService } from '@/services/events.service';
import { useEffect, useState } from 'react';

export function DeclineEventPage() {
  const { slug, token } = useParams<{ slug: string; token: string }>();
  const [declined, setDeclined] = useState(false);

  const declineMutation = useMutation({
    mutationFn: () => eventsService.declineGuest(token!),
    onSuccess: () => {
      setDeclined(true);
    },
  });

  // Fetch event details for display
  const { data: event } = useQuery({
    queryKey: ['public-event', slug],
    queryFn: () => eventsService.getBySlug(slug!),
    enabled: !!slug,
  });

  useEffect(() => {
    // Auto-decline on page load
    if (token && !declined && !declineMutation.isPending && !declineMutation.isSuccess) {
      declineMutation.mutate();
    }
  }, [token]);

  if (declineMutation.isPending) {
    return (
      <div className="min-h-screen bg-brand-ink flex items-center justify-center">
        <div className="text-white text-xl">Registrando resposta...</div>
      </div>
    );
  }

  if (declineMutation.isError) {
    return (
      <div className="min-h-screen bg-brand-ink flex items-center justify-center p-4">
        <Card className="p-8 bg-brand-smoke border-brand-gold/20 max-w-md text-center">
          <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">❌</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">Erro ao registrar resposta</h1>
          <p className="text-brand-ivory/70 mb-6">
            Não foi possível registrar sua resposta. O link pode estar inválido ou você já respondeu este convite.
          </p>
          {event && (
            <Link to={`/evento/${slug}`}>
              <Button variant="outline" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Ver detalhes do evento
              </Button>
            </Link>
          )}
        </Card>
      </div>
    );
  }

  if (declineMutation.isSuccess && declineMutation.data) {
    const { guest, event: eventData } = declineMutation.data;
    const eventDate = new Date(eventData.eventDate);
    const formattedDate = eventDate.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    return (
      <div className="min-h-screen bg-brand-ink flex items-center justify-center p-4">
        <Card className="p-8 bg-brand-smoke border-brand-gold/20 max-w-md text-center">
          <div className="w-16 h-16 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-yellow-400" />
          </div>

          <h1 className="text-3xl font-bold text-white mb-2">Resposta Registrada</h1>
          <p className="text-xl text-brand-ivory/80 mb-8">{guest.name}, sua resposta foi registrada.</p>

          <div className="bg-brand-ink/50 rounded-lg p-6 mb-8 text-left space-y-4">
            <h2 className="text-lg font-semibold text-white mb-4">{eventData.name}</h2>

            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-brand-gold mt-0.5" />
              <div>
                <p className="text-brand-ivory/60 text-sm">Data e Horário</p>
                <p className="text-white">{formattedDate}</p>
              </div>
            </div>
          </div>

          <p className="text-brand-ivory/70 mb-6">
            Sentiremos sua falta! Esperamos contar com você em uma próxima oportunidade.
          </p>

          <Link to={`/evento/${eventData.slug}`}>
            <Button className="w-full" variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Ver detalhes do evento
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  return null;
}
